import { getAccessToken } from '../authentication/supabaseService';

export const REFERRAL_STATUSES = ['pending', 'new', 'in_progress', 'won', 'lost', 'churned'] as const;
export type ReferralStatus = typeof REFERRAL_STATUSES[number];

export interface ReferralDetails {
  businessName: string;
  restaurantCount: number;
  contactName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  notes: string | null;
}

export interface AmbassadorReferral extends ReferralDetails {
  id: string;
  status: ReferralStatus;
  convertedRestaurantCount: number | null;
  commissionStartDate: string | null;
  churnedOn: string | null;
  createdAt: string;
  updatedAt: string;
  monthlyCommissionCents: number;
  potentialMonthlyCommissionCents: number;
  accumulatedCommissionCents: number;
}

export interface AdminAmbassadorReferral extends AmbassadorReferral {
  ambassadorUserId: string | null;
  ambassadorName: string | null;
  ambassadorEmail: string | null;
  monthlyPricePerRestaurantCents: number;
}

export interface ReferralKpis {
  leadsSubmitted: number;
  leadsPending: number;
  leadsClosed: number;
  monthlyCommissionCents: number;
  potentialMonthlyCommissionCents: number;
  accumulatedCommissionCents: number;
}

export interface ReferralList {
  leads: AmbassadorReferral[];
  nextCursor: string | null;
  kpis: ReferralKpis;
}

export interface AdminReferralList extends ReferralList {
  leads: AdminAmbassadorReferral[];
  ambassadors: { id: string; name: string | null; email: string | null }[];
}

export interface ReferralFilters {
  status?: ReferralStatus;
  search?: string;
  cursor?: string;
  ambassadorUserId?: string;
}

export interface AdminReferralUpdate extends ReferralDetails {
  updatedAt: string;
  status: ReferralStatus;
  convertedRestaurantCount: number | null;
  monthlyPricePerRestaurantCents: number;
  commissionStartDate: string | null;
  churnedOn: string | null;
}

export class ReferralApiError extends Error {
  constructor(public code: string, public fields: string[] = []) {
    super(code);
    this.name = 'ReferralApiError';
  }
}

async function request<T>(admin: boolean, suffix: string, options: RequestInit = {}): Promise<T> {
  const token = await getAccessToken();
  const response = await fetch(`/api/${admin ? 'admin/' : ''}ambassador-referrals${suffix}`, {
    ...options,
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new ReferralApiError(error.error || 'internal_error', error.fields || []);
  }
  return response.json();
}

export function getReferrals(admin: boolean, filters: ReferralFilters, signal?: AbortSignal): Promise<ReferralList | AdminReferralList> {
  const query = new URLSearchParams({ limit: '25' });
  Object.entries(filters).forEach(([key, value]) => { if (value) query.set(key, value); });
  return request(admin, `?${query}`, { signal });
}

export function createReferral(details: ReferralDetails): Promise<AmbassadorReferral> {
  return request(false, '', { method: 'POST', body: JSON.stringify(details) });
}

export function updateReferral(id: string, details: ReferralDetails & { updatedAt: string }): Promise<AmbassadorReferral> {
  return request(false, `/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(details) });
}

export function updateAdminReferral(id: string, details: AdminReferralUpdate): Promise<AdminAmbassadorReferral> {
  return request(true, `/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(details) });
}

export function deleteAdminReferral(id: string, updatedAt: string): Promise<{ success: true }> {
  return request(true, `/${encodeURIComponent(id)}`, { method: 'DELETE', body: JSON.stringify({ updatedAt }) });
}
